import { createFileRoute } from '@tanstack/react-router'
import { businessNumberLabel } from '@/lib/locale'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ShieldCheck, Printer } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { JURISDICTION_LABEL, hrcwLabel, type Jurisdiction } from '@/lib/hrcw'
import { SWMS_DUTY_LINES } from '@/lib/swms-templates'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/s/$token')({ component: PublicSwms })

interface PublicSwmsData {
  title: string
  version: number
  jurisdiction: Jurisdiction
  categories: string[]
  header: Record<string, string>
  steps: Array<{ task: string; hazards: string; controls: string; how: string; who: string }>
  other_hazards: string[]
  review: Record<string, string>
  rescue_plan: string | null
  ppe: string | null
  signed_at: string
  earlier_versions: number
  company: { name: string | null; abn: string | null; logo: string | null; accent_color: string | null; phone: string | null; email: string | null; country: string | null }
  signatures: Array<{ name: string; role: string; signed_at: string }>
}

const fmt = (iso: string | null) =>
  iso ? phFormat(new Date(iso), 'datetime', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

/**
 * The copy the principal contractor receives (r300). Read-only, no session, no login — a PC on
 * someone else's site is not going to create an account to read a SWMS, and a duty that is
 * inconvenient to discharge is a duty people skip.
 *
 * Shows the CURRENT signed version only. Earlier versions are acknowledged to exist but never
 * exposed: the PC needs to know the document has been revised (so they can ask why), not to read
 * a superseded set of controls and act on it.
 */
function PublicSwms() {
  const { token } = Route.useParams()

  const q = useQuery({
    queryKey: ['public-swms', token],
    queryFn: async (): Promise<PublicSwmsData | null> => {
      const { data, error } = await supabase.rpc('swms_public', { p_token: token })
      if (error) throw error
      return data as PublicSwmsData | null
    },
  })

  if (q.isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>
  }
  if (!q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <p className="text-[16px] font-semibold text-token-primary">This link is not valid</p>
          <p className="mt-1 text-[13.5px] text-token-secondary">
            It may have been withdrawn, or the SWMS may not have been signed yet. Ask the contractor for a new link.
          </p>
        </div>
      </div>
    )
  }

  const d = q.data
  const h = (k: string) => d.header?.[k] || '—'

  return (
    <div className="min-h-screen bg-token-surface py-6 print:bg-token-surface print:py-0">
      <div className="mx-auto max-w-4xl space-y-4 px-4 print:max-w-none print:px-0">
        <header className="rounded-xl border border-token-subtle bg-token-surface p-5 print:border-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-token-muted">Safe Work Method Statement</p>
              <h1 className="mt-1 text-[20px] font-bold tracking-[-0.01em] text-token-primary">{d.title}</h1>
              <p className="mt-1 text-[13px] text-token-secondary">
                {d.company.name}{d.company.abn ? ` · ${businessNumberLabel(d.company)} ${d.company.abn}` : ''}
              </p>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-token-success-surface px-3 py-1.5 text-[12.5px] font-medium text-token-success">
                <ShieldCheck className="h-4 w-4" /> Signed {fmt(d.signed_at)}
              </span>
              <p className="mt-1 text-[12px] text-token-muted">Version {d.version}</p>
              <button onClick={() => window.print()} className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-token-secondary underline print:hidden">
                <Printer className="h-3.5 w-3.5" /> Print or save as PDF
              </button>
            </div>
          </div>

          {d.earlier_versions > 0 && (
            <p className="mt-3 rounded-lg border border-token-warning bg-token-warning-surface p-2.5 text-[12.5px] text-token-warning">
              This SWMS has been revised {d.earlier_versions === 1 ? 'once' : `${d.earlier_versions} times`}. This is the
              current version; earlier ones are retained by the contractor.
            </p>
          )}
        </header>

        <Section title="Who and where">
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Row k="Direct employer (PCBU)" v={h('employer')} />
            <Row k={businessNumberLabel(d.company)} v={h('abn')} />
            <Row k="Work supervisor" v={h('supervisor')} />
            <Row k="Supervisor phone" v={h('supervisor_phone')} />
            <Row k="Principal contractor" v={h('principal_contractor')} />
            <Row k="Site location" v={h('site_address')} />
            <Row k="Work activity" v={h('work_activity')} />
            <Row k="Method" v={h('method')} />
            <Row k="Permits and licences held" v={h('permits')} />
          </dl>
        </Section>

        <Section title="High-risk construction work involved">
          <p className="mb-2 text-[12.5px] text-token-muted">Under the {JURISDICTION_LABEL[d.jurisdiction]}.</p>
          <div className="flex flex-wrap gap-1.5">
            {d.categories.map((k) => (
              <span key={k} className="rounded-full bg-token-warning-surface px-2.5 py-1 text-[12px] font-medium text-token-warning">
                {hrcwLabel(k, d.jurisdiction)}
              </span>
            ))}
          </div>
        </Section>

        <Section title="Tasks, hazards and controls">
          {/* print:overflow-visible is load-bearing, not tidiness. This table is the thing the
              principal contractor files, and an overflow-x container CLIPS in print — the PDF
              would have arrived missing "who is responsible", the column that names the person
              accountable for each control. Caught by printing it, not by reading the diff. */}
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full min-w-[700px] table-fixed border-collapse text-[12.5px] print:min-w-0 print:text-[10px]">
              <colgroup>
                <col className="w-[15%]" /><col className="w-[21%]" /><col className="w-[25%]" />
                <col className="w-[25%]" /><col className="w-[14%]" />
              </colgroup>
              <thead>
                <tr className="bg-canvas-subtle text-left text-[11.5px] font-semibold uppercase tracking-[0.04em] text-token-secondary print:text-[9px]">
                  <th className="border border-token-subtle p-2">Tasks</th>
                  <th className="border border-token-subtle p-2">Hazards and risks</th>
                  <th className="border border-token-subtle p-2">Control measures</th>
                  <th className="border border-token-subtle p-2">How they will be implemented</th>
                  <th className="border border-token-subtle p-2">Who is responsible</th>
                </tr>
              </thead>
              <tbody>
                {d.steps.map((s, i) => (
                  <tr key={i} className="align-top">
                    <td className="border border-token-subtle p-2 text-token-primary">{s.task}</td>
                    <td className="border border-token-subtle p-2 text-token-secondary">{s.hazards}</td>
                    <td className="border border-token-subtle p-2 text-token-secondary">{s.controls}</td>
                    <td className="border border-token-subtle p-2 text-token-secondary">{s.how}</td>
                    <td className="border border-token-subtle p-2 text-token-secondary">{s.who}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {d.rescue_plan && <Section title="Rescue plan"><p className="whitespace-pre-line text-[13px] leading-relaxed text-token-secondary">{d.rescue_plan}</p></Section>}
        {d.ppe && <Section title="PPE"><p className="text-[13px] leading-relaxed text-token-secondary">{d.ppe}</p></Section>}

        {d.other_hazards?.length > 0 && (
          <Section title="Other site hazards">
            <p className="mb-2 text-[12px] text-token-muted">Not high-risk construction work. Managed, and recorded here.</p>
            <ul className="space-y-1 text-[13px] text-token-secondary">{d.other_hazards.map((x, i) => <li key={i}>· {x}</li>)}</ul>
          </Section>
        )}

        <Section title="Making sure it is followed">
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Row k="Person responsible for compliance" v={d.review?.compliance_person || '—'} />
            <Row k="Review date" v={d.review?.review_date || '—'} />
            <Row k="Measures in place to ensure compliance" v={d.review?.compliance_measures || '—'} />
            <Row k="How the controls will be reviewed" v={d.review?.review_how || '—'} />
          </dl>
          <ul className="mt-3 space-y-1 rounded-lg border border-token-subtle bg-token-surface p-3 text-[12.5px] leading-relaxed text-token-secondary">
            {SWMS_DUTY_LINES.map((l) => <li key={l}>· {l}</li>)}
          </ul>
        </Section>

        <Section title="Signed on by">
          <div className="divide-y divide-token-subtle">
            {d.signatures.map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-[13px]">
                <span className="text-token-primary">{s.name}<span className="ml-2 text-[12px] text-token-muted">{s.role}</span></span>
                <span className="text-[12px] text-token-muted">{fmt(s.signed_at)}</span>
              </div>
            ))}
          </div>
        </Section>

        <p className="pb-8 text-center text-[11.5px] text-token-muted">
          Provided by {d.company.name} through MySerbisyo. The duty to prepare and comply with this SWMS rests with
          the business carrying out the work.
        </p>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-token-subtle bg-token-surface p-5 print:break-inside-avoid print:border-0">
      <h2 className="mb-3 text-[14px] font-bold text-token-primary">{title}</h2>
      {children}
    </section>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-[11.5px] font-semibold uppercase tracking-[0.04em] text-token-muted">{k}</dt>
      <dd className="text-[13px] text-token-primary">{v}</dd>
    </div>
  )
}
